/**
 * /members — public directory of Social Pulse members with completed profiles.
 *
 * A "completed profile" = has both a gender AND a profession set.
 *
 * Shows name, profile image (default avatar if none), profession, location,
 * verification badge, and a short about-preview (250 char) for public
 * profiles. Private profiles show only name / avatar / location / profession
 * with a subtle "Private" tag.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Search, MapPin, Shield, Lock, Loader2 } from "lucide-react";
import Avatar from "@/components/Avatar";
import PeopleYouMayKnow from "@/components/PeopleYouMayKnow";

const GENDERS = [
  { v: "", label: "All" },
  { v: "male", label: "Male" },
  { v: "female", label: "Female" },
  { v: "other", label: "Other" },
];

export default function Members() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (gender) params.set("gender", gender);
    params.set("limit", "48");
    http.get(`/members?${params.toString()}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [q, gender]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="members-page">
      <div className="overline">SOCIAL PULSE · COMMUNITY</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2">Our Members</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Meet the people building this community. Only members who have completed their profile appear here.
        {" "}<Link to="/profile/edit" className="underline" style={{ color: "var(--wofy-primary)" }}>Complete yours →</Link>
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60" />
          <input
            className="wofy-input pl-10"
            placeholder="Search by name, profession, or handle…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="members-search"
          />
        </div>
        <div className="flex items-center gap-1">
          {GENDERS.map((g) => (
            <button
              key={g.v}
              onClick={() => setGender(g.v)}
              data-testid={`members-gender-${g.v || "all"}`}
              className="px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-colors"
              style={gender === g.v
                ? { background: "var(--wofy-primary)", color: "#FDFBF7" }
                : { border: "1px solid var(--wofy-border)", color: "var(--wofy-text-soft)" }
              }
            >{g.label}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 text-xs opacity-70">
        {loading ? "Loading…" : `${total} member${total === 1 ? "" : "s"}`}
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading community…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="mt-10 wofy-card text-center py-14">
          <div className="font-display text-2xl">No members yet</div>
          <p className="text-sm mt-2" style={{ color: "var(--wofy-text-soft)" }}>
            Be the first to complete your profile with gender + profession.
          </p>
          <Link to="/profile/edit" className="wofy-btn-primary mt-5 inline-flex">Complete my profile</Link>
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((m) => {
          const isPrivate = m.profile_visibility === "private";
          return (
            <Link
              key={m.id}
              to={m.handle ? `/u/${m.handle}` : "#"}
              className="wofy-card !p-5 flex items-start gap-4 hover:shadow-md transition-shadow"
              data-testid={`member-${m.handle || m.id}`}
            >
              <Avatar src={m.avatar_url} name={m.name} gender={m.gender} size={64} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-display text-lg truncate">{m.name || "—"}</span>
                  {m.verification_tier && <Shield className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--wofy-primary)" }} />}
                  {isPrivate && <Lock className="w-3 h-3 opacity-50 shrink-0" title="Private profile" />}
                </div>
                {m.profession && <div className="text-sm mt-0.5" style={{ color: "var(--wofy-text)" }}>{m.profession}</div>}
                {m.location && (
                  <div className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
                    <MapPin className="w-3 h-3" /> {m.location}
                  </div>
                )}
                {m.about_preview && !isPrivate && (
                  <div className="text-xs mt-2 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
                    {m.about_preview}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
